var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import classNames from "classnames";
import useDebounce from "@hooks/useDebounce";
import useHasChanged from "@hooks/useHasChanged";
import SearchBox from "@widget/forms/inputs/SearchBox";
import SortableTreeNode from './SortableTreeNode';
import { hasValue } from "@utils";
import { UXPContext } from "@core/context/Context";
import { TreeViewLoader, LoadingSpinner } from "@widget/loaders";
import useDragSensors from '@hooks/touch/useDragSensors';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { getNodeDepth, canDropIntoNode, customCollisionDetection } from './treeview-drag-utils';
import { extractTreeViewStyles, findTreeNodeByIdSync, processAllTreeNodes, calculateDropPosition, generateIdWithPrefix } from "./treeview-utils";
import Icon from "@icon/Icon";
import ActionsSheet from "@widget/popups/ActionsSheet";
import ButtonComponent from "@widget/buttons/ButtonComponent";
/**
 * How many times a reload merge may be retried when the user changes the tree (expand / collapse)
 * while it runs. After that the merged result is kept and the on-screen expansion applied over it.
 */
const MAX_ITEM_MERGE_ATTEMPTS = 3;
/**
 *
 * @export
 *
 * TreeView component renders a hierarchical structure of nodes with optional icons, labels, and selectable functionality.
 *
 * @example
 *
 * ```
 * <TreeView
 *    items={[
 *      { id: '1', label: 'Root Node', children: [{ id: '1-1', label: 'Child Node' }] }
 *    ]}
 *    selected={selectedNode}
 *    onSelect={handleSelectNode}
 *    showPath={true}
 *    styles={{
 *      nodeBGColor: '#f0f0f0',
 *      labelTextColor: '#333',
 *    }}
 * />
 * ```
 *
 * A node with `isLabel` is a group label: it is never selected, a click on its row expands or
 * collapses it, and it reads as a heading (bold, secondary text colour, no fill).
 *
 * @example
 * ```tsx
 * <TreeView showPath items={[{ id: 'b1', label: 'IBMS 2026 · Intake A', icon: 'fal users', path: 'b1', isLabel: true, expandOnLoad: true, getChildren: [{ id: 'c1', label: 'Building Automation 101', icon: 'fal book', path: 'b1/c1', isLabel: true, expandOnLoad: true, getChildren: [{ id: 'cd', label: 'Course discussion', icon: 'fal comments', path: 'b1/c1/cd' }, { id: 'l1', label: 'Sensors', icon: 'fal file-lines', path: 'b1/c1/l1' }] }] }]} />
 * ```
 *
 * @category Data Display/Lists/Tree View
 */
const TreeView = forwardRef((props, ref) => {
    var _a;
    const context = useContext(UXPContext);
    const [loading, setLoading] = useState(false);
    const [nodes, setNodesState] = useState([]);
    /**
     * The tree as it stands right now.
     *
     * Anything that reads the tree outside of the render that created it - the imperative handle
     * (created once, deps `[]`), or an async handler that continues after an `await` - must read
     * this instead of the `nodes` state, which is frozen at its own render.
     */
    const nodesRef = useRef([]);
    /**
     * Bumped on every write to the tree, so an async merge can tell whether the tree moved under
     * it - the user expanding or collapsing a node while a background reload is in flight.
     */
    const nodesVersionRef = useRef(0);
    /**
     * Every write to the tree goes through here so `nodesRef` is updated synchronously, before
     * React re-renders. Accepts a value or an updater, exactly like `useState`'s setter.
     */
    const setNodes = useCallback((value) => {
        const next = typeof value === 'function'
            ? value(nodesRef.current)
            : value;
        nodesRef.current = next;
        nodesVersionRef.current++;
        setNodesState(next);
    }, []);
    /**
     * Items can be swapped faster than `processAllTreeNodes` resolves (two saves in a row, a
     * refetch landing on top of another). Only the newest request may write to the tree.
     */
    const itemsRequestRef = useRef(0);
    const [searchText, setSearchText] = useState('');
    const [internalDragState, setInternalDragState] = useState({
        isDragging: false,
        draggedNodeId: null,
        dropzoneNodeId: null,
        isDragSource: true,
        draggedNode: null,
        dropPosition: null
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [mousePosition, setMousePosition] = useState(null);
    const query = useDebounce(searchText, 300);
    const hasItemsChanged = useHasChanged(props.items);
    // Mouse: an 8px move starts a drag, so a click still selects.
    // Touch: press and hold to drag, so a swipe or a scroll never starts one.
    const sensors = useDragSensors();
    // Touch: no hover, so a node's actions open in a sheet (swipe the node left, or its ⋯)
    const [actionsSheet, setActionsSheet] = useState(null);
    const lastDragEnd = useRef(0);
    const enableMultiSelect = (props === null || props === void 0 ? void 0 : props.multiSelect) || false;
    const selectedItems = enableMultiSelect ? (!!(props === null || props === void 0 ? void 0 : props.selected) && (props === null || props === void 0 ? void 0 : props.selected) instanceof Array && ((_a = props === null || props === void 0 ? void 0 : props.selected) === null || _a === void 0 ? void 0 : _a.length) > 0) ? props.selected : [] : (props === null || props === void 0 ? void 0 : props.selected) || null;
    // Check if drag & drop should be enabled
    const isDragDropEnabled = (props.enableDrag || props.enableReordering || props.enableHierarchyChange) &&
        !(enableMultiSelect && (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) > 1);
    // Use external loading if provided, otherwise use internal loading state
    const isLoading = props.loading ? props.loading : loading;
    const isOperationLoading = isLoading || isProcessing || props.reorderLoading;
    // Calculate all path-related values in JavaScript for better browser compatibility
    const treeViewStyles = extractTreeViewStyles(props, context);
    // Drag state management for both internal and cross-tree drag operations
    // When externalDragState is provided (cross-tree scenario):
    //   - isDragging: true indicates ANY drag is happening globally
    //   - isDragSource: true means THIS tree is being dragged FROM
    // This allows target trees to show drop indicators even though they're not the drag source
    const dragState = (props === null || props === void 0 ? void 0 : props.externalDragState) || internalDragState;
    useEffect(() => {
        if (hasItemsChanged)
            processInitialItems();
    }, [props.items, hasItemsChanged]);
    useEffect(() => {
        var _a;
        if ((props === null || props === void 0 ? void 0 : props.enableSearch) && !!(props === null || props === void 0 ? void 0 : props.onSearch) && typeof (props === null || props === void 0 ? void 0 : props.onSearch) == 'function') {
            (_a = props === null || props === void 0 ? void 0 : props.onSearch) === null || _a === void 0 ? void 0 : _a.call(props, query);
        }
    }, [query]);
    // Track mouse position during drag for accurate drop position calculation
    useEffect(() => {
        // Only track during drag
        if (!dragState.isDragging) {
            setMousePosition(null);
            return;
        }
        const handlePointerMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        document.addEventListener('pointermove', handlePointerMove);
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
        };
    }, [dragState.isDragging]);
    /**
     * Copy the expand/collapse state of the tree on screen onto a freshly merged one, by node id.
     * The last resort when the user keeps toggling nodes while a reload merges: their choice wins.
     */
    function applyExpandedState(merged, current) {
        return merged.map(node => {
            var _a;
            const onScreen = findTreeNodeByIdSync(current, node.id);
            return Object.assign(Object.assign({}, node), { expanded: onScreen ? !!onScreen.expanded : node.expanded, children: (((_a = node.children) === null || _a === void 0 ? void 0 : _a.length) > 0) ? applyExpandedState(node.children, current) : node.children });
        });
    }
    function processInitialItems() {
        // On first load (no nodes yet) show the loading skeleton.
        // On subsequent updates pass the existing nodes so expand/collapse state
        // and scroll position are preserved (no content flash).
        // The merge reads `nodesRef`, not the `nodes` of the render that scheduled this call, so
        // two swaps in quick succession still merge against what is actually on screen.
        const isFirstLoad = nodesRef.current.length === 0;
        if (isFirstLoad)
            setLoading(true);
        const requestId = ++itemsRequestRef.current;
        const merge = () => __awaiter(this, void 0, void 0, function* () {
            let merged = [];
            // Merge against the tree as it stands; if it moved under us, merge again against the
            // newer one so a change is not reverted. The tree can move under us when a consumer
            // swaps `items` without passing `loading` - no overlay goes up, so the user is still
            // free to expand or collapse a node while this merge runs.
            for (let attempt = 0; attempt < MAX_ITEM_MERGE_ATTEMPTS; attempt++) {
                const versionBefore = nodesVersionRef.current;
                merged = yield processAllTreeNodes(nodesRef.current, props.items, props === null || props === void 0 ? void 0 : props.expandAllNodes);
                // A newer swap started while this one was resolving - let that one win.
                if (requestId !== itemsRequestRef.current)
                    return;
                if (nodesVersionRef.current === versionBefore) {
                    setNodes(merged);
                    setLoading(false);
                    return;
                }
            }
            // Still moving after several tries: keep this result, but let what is on screen decide
            // which nodes are open.
            setNodes(applyExpandedState(merged, nodesRef.current));
            setLoading(false);
        });
        merge().catch(() => {
            if (requestId !== itemsRequestRef.current)
                return;
            setNodes([]);
            setLoading(false);
        });
    }
    function handleSelect(node, isDisabled, hasSeleceted) {
        var _a, _b, _c;
        // A label node groups other nodes; it is never selected
        if (node === null || node === void 0 ? void 0 : node.isLabel)
            return;
        if (!isDisabled) {
            if (enableMultiSelect) {
                if (hasSeleceted) {
                    (_a = props === null || props === void 0 ? void 0 : props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, (selectedItems || []).filter(n => n.id !== node.id));
                }
                else {
                    (_b = props === null || props === void 0 ? void 0 : props.onSelect) === null || _b === void 0 ? void 0 : _b.call(props, [...selectedItems, node]);
                }
            }
            else {
                (_c = props === null || props === void 0 ? void 0 : props.onSelect) === null || _c === void 0 ? void 0 : _c.call(props, node);
            }
        }
    }
    function handleToggleNode(node) {
        return __awaiter(this, void 0, void 0, function* () {
            // Always patch the tree as it stands now: a reload may have replaced it while the
            // children of this node were loading.
            try {
                // Set the node's expanding state to true
                const updatedNodes = updateNodeState(nodesRef.current, node.id, { expanding: true });
                setNodes(updatedNodes);
                // If the node is already expanded, collapse it
                if (node.expanded) {
                    const collapsedNodes = updateNodeState(nodesRef.current, node.id, { expanded: false, expanding: false });
                    setNodes(collapsedNodes);
                    return;
                }
                // If the node has no children and getChildren is a function, load the children
                if (node.getChildren && typeof node.getChildren === 'function' && node.children.length === 0) {
                    try {
                        const result = yield node.getChildren(node);
                        const updatedNodes = updateNodeState(nodesRef.current, node.id, {
                            children: yield processAllTreeNodes(nodesRef.current, result.nodes, props === null || props === void 0 ? void 0 : props.expandAllNodes),
                            expanded: true,
                            expanding: false,
                        });
                        setNodes(updatedNodes);
                    }
                    catch (error) {
                        console.error(`Unable to load children for node ${node.id}. Error: `, error);
                        const updatedNodes = updateNodeState(nodesRef.current, node.id, { expanding: false });
                        setNodes(updatedNodes);
                    }
                }
                else {
                    // If children are already available, simply toggle expansion
                    const updatedNodes = updateNodeState(nodesRef.current, node.id, { expanded: true, expanding: false });
                    setNodes(updatedNodes);
                }
            }
            catch (error) {
                console.error(`Unable to toggle node ${node.id}. Error: `, error);
                const updatedNodes = updateNodeState(nodesRef.current, node.id, { expanding: false });
                setNodes(updatedNodes);
            }
        });
    }
    function updateNodeState(nodes, nodeId, updatedProperties) {
        return nodes.map((node) => {
            if (node.id === nodeId) {
                // Update the node properties
                return Object.assign(Object.assign({}, node), updatedProperties);
            }
            else if (node.children && node.children.length > 0) {
                // Recursively update children
                return Object.assign(Object.assign({}, node), { children: updateNodeState(node.children, nodeId, updatedProperties) });
            }
            else {
                return node;
            }
        });
    }
    const resetInternalDragState = () => {
        setInternalDragState({
            isDragging: false,
            draggedNodeId: null,
            dropzoneNodeId: null,
            isDragSource: true,
            draggedNode: null,
            dropPosition: null
        });
    };
    function setAllExpanded(nodes, expanded) {
        return nodes.map(n => (Object.assign(Object.assign({}, n), { expanded, children: n.children ? setAllExpanded(n.children, expanded) : n.children })));
    }
    /**
     * The handle below is created once (deps `[]`), so the `processDrop` it closes over would be
     * the one from the first render - reading an empty tree and the first render's props. Keep a
     * ref pointing at the current one instead.
     */
    const processDropRef = useRef(null);
    useImperativeHandle(ref, () => ({
        triggerDrop: (sourceNodeId, targetNodeId, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            yield ((_a = processDropRef.current) === null || _a === void 0 ? void 0 : _a.call(processDropRef, sourceNodeId, targetNodeId, dropPosition));
        }),
        expandAll: () => setNodes(prev => setAllExpanded(prev, true)),
        collapseAll: () => setNodes(prev => setAllExpanded(prev, false)),
    }), []);
    const handleDragStart = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const nodeId = ((_a = event === null || event === void 0 ? void 0 : event.active) === null || _a === void 0 ? void 0 : _a.id) || null;
        if (!nodeId)
            return;
        const node = findTreeNodeByIdSync(nodes, nodeId);
        setInternalDragState({
            isDragging: true,
            draggedNodeId: nodeId,
            draggedNode: node,
            dropPosition: null
        });
    });
    /**
     * A drop the consumer refuses (props.canDrop) must not even show an indicator, so the
     * position is dropped while dragging and the target is marked invalid instead.
     */
    const isDropRefused = (target, position, source) => {
        if (!position || !props.canDrop)
            return false;
        const dragged = source || internalDragState.draggedNode;
        if (!dragged)
            return false;
        return !props.canDrop(dragged, target, position);
    };
    const handleDragOver = (event) => {
        var _a, _b;
        const targetId = (_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id;
        if (!targetId)
            return;
        const targetNode = findTreeNodeByIdSync(nodes, targetId);
        if (!targetNode)
            return;
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // Always use 30/30/40 split so "child" is reachable even for siblings
            dropPosition = calculateDropPosition(mousePosition.y, overRect, false);
        }
        const refused = isDropRefused(targetNode, dropPosition);
        setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropzoneNodeId: targetId, dropPosition: refused ? null : dropPosition, dropRefused: refused })));
    };
    const handleDragMove = (event) => {
        var _a, _b;
        const targetId = (_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id;
        // If not over any node, clear drop position
        if (!targetId) {
            setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: null, dropRefused: false })));
            return;
        }
        const targetNode = findTreeNodeByIdSync(nodes, targetId);
        if (!targetNode)
            return;
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // Always use 30/30/40 split so "child" is reachable even for siblings
            dropPosition = calculateDropPosition(mousePosition.y, overRect, false);
        }
        const refused = isDropRefused(targetNode, dropPosition);
        // Only update drop position (don't change dropzoneNodeId)
        setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: refused ? null : dropPosition, dropRefused: refused })));
    };
    /**
     * Process a drop operation - used by both handleDragEnd and triggerDrop
     */
    const processDrop = (sourceNodeId, targetNodeId, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
        // Validate source and target are not the same
        if (sourceNodeId === targetNodeId) {
            return;
        }
        // Get actual nodes - from the current tree, since this also runs from the imperative
        // handle's triggerDrop, whose closure predates every reload.
        const sourceNode = findTreeNodeByIdSync(nodesRef.current, sourceNodeId);
        const targetNode = findTreeNodeByIdSync(nodesRef.current, targetNodeId);
        // Validate nodes and dropPosition exist
        if (!targetNode || !sourceNode || !dropPosition) {
            return;
        }
        // The consumer's veto - the single gate for both dragging and triggerDrop
        if (props.canDrop && !props.canDrop(sourceNode, targetNode, dropPosition)) {
            return;
        }
        // Handle different drop types based on drop position
        try {
            switch (dropPosition) {
                case 'before':
                    yield handleInsertBefore(sourceNode, targetNode);
                    break;
                case 'after':
                    yield handleInsertAfter(sourceNode, targetNode);
                    break;
                case 'child':
                    yield handleAddAsChild(sourceNode, targetNode);
                    break;
            }
        }
        catch (error) {
            console.error('Error during drop processing:', error);
            throw error;
        }
    });
    // Keep the imperative handle's triggerDrop pointing at this render's processDrop.
    useEffect(() => {
        processDropRef.current = processDrop;
    });
    const handleDragEnd = (event) => __awaiter(void 0, void 0, void 0, function* () {
        lastDragEnd.current = Date.now();
        const { active: source, over: target } = event;
        const { dropPosition } = internalDragState;
        try {
            // no target or no dropPosition - cancel drag
            if (!target || !dropPosition) {
                return;
            }
            yield processDrop(source.id, target.id, dropPosition);
        }
        catch (error) {
            console.error('Error during drag end:', error);
        }
        finally {
            resetInternalDragState();
        }
    });
    // Helper function to get node index within its siblings
    function getNodeIndex(node) {
        const parentId = node.parentId;
        let siblings;
        if (parentId) {
            const parent = findTreeNodeByIdSync(nodesRef.current, parentId);
            siblings = (parent === null || parent === void 0 ? void 0 : parent.children) || [];
        }
        else {
            siblings = nodesRef.current;
        }
        return siblings.findIndex(n => n.id === node.id);
    }
    const handleInsertBefore = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        // Insert sourceNode before targetNode (as sibling)
        if (props.enableReordering && sourceNode.parentId === targetNode.parentId) {
            // Same parent - reorder
            yield handleReorder(sourceNode, targetNode, 'before');
        }
        else if (props.enableHierarchyChange && props.onHierarchyChange) {
            // Different parent - hierarchy change, insert at target's index
            yield props.onHierarchyChange(sourceNode, targetNode.parentId || null, getNodeIndex(targetNode));
        }
    });
    const handleInsertAfter = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        // Insert sourceNode after targetNode (as sibling)
        if (props.enableReordering && sourceNode.parentId === targetNode.parentId) {
            // Same parent - reorder
            yield handleReorder(sourceNode, targetNode, 'after');
        }
        else if (props.enableHierarchyChange && props.onHierarchyChange) {
            // Different parent - hierarchy change, insert after target
            yield props.onHierarchyChange(sourceNode, targetNode.parentId || null, getNodeIndex(targetNode) + 1);
        }
    });
    const handleAddAsChild = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        // Add sourceNode as last child of targetNode
        if (props.enableHierarchyChange && props.onHierarchyChange) {
            if (canDropIntoNode(sourceNode, targetNode, nodesRef.current, props.maxDepth)) {
                yield props.onHierarchyChange(sourceNode, targetNode.id, ((_a = targetNode.children) === null || _a === void 0 ? void 0 : _a.length) || 0);
            }
            else {
                console.warn('Invalid drop: Cannot move node to this location (max depth or circular reference)');
            }
        }
    });
    const handleReorder = (sourceNode, targetNode, position) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsProcessing(true);
            // Find the parent and its children
            const parentId = sourceNode.parentId;
            let siblings;
            if (parentId) {
                const parent = findTreeNodeByIdSync(nodesRef.current, parentId);
                if (!parent)
                    return;
                siblings = parent.children;
            }
            else {
                // Root level
                siblings = nodesRef.current;
            }
            // Calculate indices for reordering
            const oldIndex = siblings.findIndex(n => n.id === sourceNode.id);
            const targetIndex = siblings.findIndex(n => n.id === targetNode.id);
            // Adjust target index based on position
            // For 'after': when oldIndex < targetIndex, target shifts left after removal, so use targetIndex
            //              when oldIndex > targetIndex, target position unchanged, so use targetIndex + 1
            const adjustedIndex = position === 'after'
                ? (oldIndex < targetIndex ? targetIndex : targetIndex + 1)
                : targetIndex;
            // Reorder siblings using arrayMove
            const reordered = arrayMove(siblings, oldIndex, adjustedIndex);
            const newIndex = reordered.findIndex(n => n.id === sourceNode.id);
            // Call the callback
            if (props.onReorder) {
                const done = yield props.onReorder(reordered, sourceNode, newIndex, parentId);
                if (done) {
                    if (parentId) {
                        const updatedNodes = updateNodeState(nodesRef.current, parentId, { children: reordered });
                        setNodes(updatedNodes);
                    }
                    else {
                        setNodes(reordered);
                    }
                }
            }
        }
        catch (error) {
            console.error('Error during reorder:', error);
        }
        finally {
            setIsProcessing(false);
        }
    });
    function renderAddRoot() {
        if (!(props === null || props === void 0 ? void 0 : props.enableAdd))
            return null;
        return _jsxs("div", { className: classNames("uxpcore_treeview__add-root-node"), onClick: () => {
                var _a;
                (_a = props === null || props === void 0 ? void 0 : props.onAdd) === null || _a === void 0 ? void 0 : _a.call(props);
            }, children: ["+ ", (props === null || props === void 0 ? void 0 : props.addButtonLabel) || 'add'] });
    }
    // What a node offers: add a child, edit, delete and the custom actions
    function getNodeActions(node) {
        var _a;
        // Check depth restriction for adding children
        let canAddChildren = ((node === null || node === void 0 ? void 0 : node.enableAddChildren) !== false) && (node.enableAddChildren || props.enableAdd);
        if (canAddChildren && props.maxDepth !== undefined) {
            const nodeDepth = getNodeDepth(node.id, nodes);
            if (nodeDepth >= props.maxDepth) {
                canAddChildren = false; // At max depth, can't add children
            }
        }
        const canEdit = ((node === null || node === void 0 ? void 0 : node.enableEditNode) !== false) && (node.enableEditNode || props.enableEdit);
        const canDelete = ((node === null || node === void 0 ? void 0 : node.enableDeleteNode) !== false) && (node.enableDeleteNode || props.enableDelete);
        const custom = ((_a = props === null || props === void 0 ? void 0 : props.customActions) === null || _a === void 0 ? void 0 : _a.call(props, node)) || [];
        const any = !!(canAddChildren || canEdit || canDelete || custom.length);
        return { canAddChildren, canEdit, canDelete, custom, any };
    }
    function renderActionButtons(node) {
        const { canAddChildren, canEdit, canDelete, custom } = getNodeActions(node);
        const hasCustomActions = !!(props === null || props === void 0 ? void 0 : props.customActions);
        if (!canEdit && !canDelete && !canAddChildren && !hasCustomActions)
            return null;
        // Touch: one action shows; two or more go behind ⋯ (or a swipe), into the actions sheet
        const count = [canAddChildren, canEdit, canDelete].filter(Boolean).length + custom.length;
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: classNames("uxpcore_treeview__node-actions", { 'uxpcore_treeview__node-actions--show': !props.showActionButtonOnlyOnHover, 'uxpcore_treeview__node-actions--many': count > 1 }), children: [canAddChildren ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                                var _a;
                                e.preventDefault();
                                e.stopPropagation();
                                (_a = props === null || props === void 0 ? void 0 : props.onAdd) === null || _a === void 0 ? void 0 : _a.call(props, node);
                            }, children: _jsx(Icon, { icon: ['fal', 'plus'] }) }) : null, canEdit ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                                var _a;
                                e.preventDefault();
                                e.stopPropagation();
                                (_a = props === null || props === void 0 ? void 0 : props.onEdit) === null || _a === void 0 ? void 0 : _a.call(props, node);
                            }, children: _jsx(Icon, { icon: ['fal', 'pencil'] }) }) : null, canDelete ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                                var _a;
                                e.preventDefault();
                                e.stopPropagation();
                                (_a = props === null || props === void 0 ? void 0 : props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props, node.id);
                            }, children: _jsx(Icon, { icon: ['fal', 'trash'] }) }) : null, custom.map((action, index) => (_jsx("div", { className: classNames("uxpcore_treeview__node-action-btn"), onClick: (e) => {
                                var _a;
                                e.preventDefault();
                                e.stopPropagation();
                                (_a = action === null || action === void 0 ? void 0 : action.onClick) === null || _a === void 0 ? void 0 : _a.call(action, node);
                            }, title: action.tooltip, children: _jsx(Icon, { icon: action.icon }) }, index)))] }), count > 1 && _jsx("div", { className: "uxpcore_treeview__node-more", role: "button", "aria-label": context.$L('uxp-core.text.actions'), onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openActionsSheet(node);
                    }, children: _jsx(Icon, { icon: ['phr', 'dots-three'] }) })] });
    }
    // Phones: swiping a node left opens its actions (not right after a drag ends)
    const openActionsSheet = (node) => {
        if (Date.now() - lastDragEnd.current < 400 || !getNodeActions(node).any)
            return;
        setActionsSheet({ node, open: true });
    };
    const closeActionsSheet = () => setActionsSheet(s => s && Object.assign(Object.assign({}, s), { open: false }));
    function renderActionsSheet() {
        if (!actionsSheet)
            return null;
        const node = actionsSheet.node;
        const { canAddChildren, canEdit, canDelete, custom } = getNodeActions(node);
        // The sheet closes when a row is tapped, before its action runs
        return _jsxs(ActionsSheet, { isOpen: actionsSheet.open, onClose: closeActionsSheet, title: node.label, children: [canAddChildren && _jsx(ButtonComponent, { title: context.$L('uxp-core.text.add'), leftIcon: "phl plus", variant: "secondary", onClick: () => { var _a; return (_a = props === null || props === void 0 ? void 0 : props.onAdd) === null || _a === void 0 ? void 0 : _a.call(props, node); } }), canEdit && _jsx(ButtonComponent, { title: context.$L('uxp-core.text.edit'), leftIcon: "phl pencil-simple", variant: "secondary", onClick: () => { var _a; return (_a = props === null || props === void 0 ? void 0 : props.onEdit) === null || _a === void 0 ? void 0 : _a.call(props, node); } }), custom.map((action, index) => _jsx(ButtonComponent, { title: action.tooltip, leftIcon: action.icon, variant: "secondary", onClick: () => { var _a; return (_a = action === null || action === void 0 ? void 0 : action.onClick) === null || _a === void 0 ? void 0 : _a.call(action, node); } }, index)), canDelete && _jsx(ButtonComponent, { title: context.$L('uxp-core.text.delete'), leftIcon: "phl trash", variant: "danger", onClick: () => { var _a; return (_a = props === null || props === void 0 ? void 0 : props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props, node.id); } })] });
    }
    function renderIcon(icon) {
        if (!hasValue(icon))
            return null;
        return _jsx("div", { className: "uxpcore_treeview__node-icon-container", children: _jsx(Icon, { icon: icon }) });
    }
    function renderNodes(_nodes, childNodes, isParentDisabled) {
        var _a;
        // Wrap nodes in SortableContext for drag & drop
        const nodeIds = _nodes.map(n => generateIdWithPrefix(n.id, props.idPrefixForComparison));
        const effectiveDraggedNode = ((_a = props.externalDragState) === null || _a === void 0 ? void 0 : _a.draggedNodeId)
            ? findTreeNodeByIdSync(nodes, props.externalDragState.draggedNodeId) || {
                id: props.externalDragState.draggedNodeId,
                label: 'External Node',
                nodeId: props.externalDragState.draggedNodeId,
                children: [],
                path: '',
            }
            : internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode;
        const renderedNodes = (_nodes || []).map((node, i) => (_jsx(SortableTreeNode, { node: node, childNodes: childNodes, isParentDisabled: isParentDisabled, isLastNode: i === (_nodes || []).length - 1, isFirstNode: i === 0, isDragDropEnabled: isDragDropEnabled, isOperationLoading: isOperationLoading, overId: dragState.dropzoneNodeId ? generateIdWithPrefix(dragState.dropzoneNodeId, props.idPrefixForComparison) : null, isDragging: dragState.isDragging, draggedNode: effectiveDraggedNode, allNodes: nodes, maxDepth: props.maxDepth, enableHierarchyChange: props.enableHierarchyChange, enableMultiSelect: enableMultiSelect, selectedItems: selectedItems, actionButtonsPosition: props.actionButtonsPosition, renderIcon: renderIcon, handleToggleNode: handleToggleNode, handleSelect: handleSelect, renderActionButtons: renderActionButtons, onSwipeActions: openActionsSheet, renderNodes: renderNodes, renderLabel: props.renderLabel, renderDetails: props.renderDetails, styles: props.styles, isCrossTreeDrag: !!props.externalDragState && !props.externalDragState.isDragSource, dropPosition: dragState.dropPosition, isDropRefused: dragState.dropRefused, idPrefixForComparison: props === null || props === void 0 ? void 0 : props.idPrefixForComparison }, node.id)));
        // Wrap in SortableContext if drag & drop is enabled
        if (isDragDropEnabled) {
            return (_jsx(SortableContext, { items: nodeIds, strategy: verticalListSortingStrategy, children: renderedNodes }));
        }
        return renderedNodes;
    }
    const treeContent = _jsxs("div", { className: classNames("uxpcore_treeview__container", {
            'uxpcore_treeview__container--show-path': (props === null || props === void 0 ? void 0 : props.showPath) != false,
            'uxpcore_treeview__container--horizontal-roots': props === null || props === void 0 ? void 0 : props.renderRootNodesHorizontally,
        }), style: treeViewStyles, children: [(props === null || props === void 0 ? void 0 : props.showHeader)
                ? _jsxs("div", { className: "uxpcore_treeview__header", children: [_jsx("div", { className: "uxpcore_treeview__title", children: !!(props === null || props === void 0 ? void 0 : props.title) ? props.title : null }), _jsx("div", { className: "uxpcore_treeview__search", children: (props === null || props === void 0 ? void 0 : props.enableSearch)
                                ? _jsx(SearchBox, { value: searchText, onChange: setSearchText })
                                : null })] })
                : null, (isLoading && ((nodes === null || nodes === void 0 ? void 0 : nodes.length) || 0) === 0) ? _jsx(_Fragment, { children: _jsx(TreeViewLoader, {}) })
                : _jsx(_Fragment, { children: ((nodes === null || nodes === void 0 ? void 0 : nodes.length) == 0)
                        ? _jsxs("div", { className: "uxpcore_treeview__noitems", children: [hasValue(props.noItemsMessage) ? props.noItemsMessage : 'No items found', renderAddRoot()] })
                        : _jsx("div", { className: "uxpcore_treeview__content", children: _jsxs("div", { className: "uxpcore_treeview__tree", children: [renderNodes(nodes || []), renderAddRoot()] }) }) }), (isLoading && ((nodes === null || nodes === void 0 ? void 0 : nodes.length) || 0) > 0)
                ? _jsx("div", { className: "uxpcore_treeview__reordering-loading", children: _jsx(LoadingSpinner, { spinnerOnly: true }) })
                : null, renderActionsSheet()] });
    // Wrap in DndContext if drag & drop is enabled
    // For cross-tree drag operations, disableInternalDndContext is true and the parent
    // component (e.g., TreeViewCompare) provides a single DndContext for both trees
    // This ensures collision detection works across tree boundaries
    if (isDragDropEnabled && !(props === null || props === void 0 ? void 0 : props.disableInternalDndContext)) {
        return (_jsxs(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragMove: handleDragMove, onDragEnd: handleDragEnd, children: [treeContent, _jsx(DragOverlay, { children: (internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode) ? (_jsx("div", { className: "uxpcore_treeview__drag-overlay", children: _jsx("div", { className: "uxpcore_treeview__drag-overlay-content", children: _jsx("div", { className: "uxpcore_treeview__drag-overlay-label", children: internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode.label }) }) })) : null }), isProcessing || props.reorderLoading
                    ? _jsx("div", { className: "uxpcore_treeview__reordering-loading", children: _jsx(TreeViewLoader, {}) })
                    : null] }));
    }
    return treeContent;
});
export default TreeView;
